"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
  type JSX,
} from "react";
import type Lenis from "lenis";

/**
 * Site-wide inertial scrolling.
 *
 * Lenis is left in its default `wrapper: window` mode, so the document keeps
 * doing real native scrolling — Lenis only smooths *how fast* the scroll
 * position gets to its target. That matters here: the site leans on GSAP
 * ScrollTrigger `pin: true` (home-v3 and the HK formation timeline), which
 * pins with `position: fixed`. A transform-based smooth-scroll wrapper would
 * break those pins; window mode leaves them measuring against the viewport
 * exactly as before. It also means every existing `window.addEventListener
 * ("scroll")` / `window.scrollY` reader (Navbar, StickyBottomCta,
 * FloatingCTABar, CrispNudges) keeps working untouched, because native scroll
 * events still fire.
 */

type LenisRef = MutableRefObject<Lenis | null>;

// Stable fallback so `useLenis()` outside the provider is a no-op rather than
// a crash — the ref shape means consumers always write `?.` anyway.
const DETACHED_REF: LenisRef = { current: null };

const SmoothScrollContext = createContext<LenisRef>(DETACHED_REF);

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Handle on the live Lenis instance, as a ref rather than state: the instance
 * is created asynchronously (see below) and putting it in state would
 * re-render the entire application tree once it lands. Read `.current` inside
 * effects/handlers, never during render.
 */
export const useLenis = (): LenisRef => useContext(SmoothScrollContext);

/**
 * Freeze the page behind an overlay. Lenis swallows wheel/touch itself while
 * stopped, so this is the piece a CSS `overflow: hidden` lock can no longer
 * cover on its own.
 */
export const useLenisScrollLock = (locked: boolean): void => {
  const lenisRef = useLenis();

  useEffect(() => {
    if (!locked) return;

    // The instance may not exist yet (reduced motion, /admin, or the dynamic
    // import still in flight) — the CSS lock at the call site covers that.
    lenisRef.current?.stop();

    return () => {
      // Reading `.current` at cleanup time is the point, not an oversight: the
      // provider can have torn down and rebuilt the instance while the overlay
      // was open (a reduced-motion toggle), and calling `start()` on the
      // captured, already-destroyed one would silently do nothing.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      lenisRef.current?.start();
    };
  }, [locked, lenisRef]);
};

export const SmoothScrollProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const lenisRef = useRef<Lenis | null>(null);
  const pathname = usePathname();

  // The admin CMS is a dense tool with its own scroll containers and a TipTap
  // editor; inertial scrolling there is a liability, not a feature.
  const enabled = !pathname?.startsWith("/admin");

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let teardown: (() => void) | null = null;

    const start = async (): Promise<void> => {
      // Dynamic so Lenis + GSAP + ScrollTrigger stay out of the shared entry
      // chunk: only four components import GSAP statically, and this provider
      // sits on every route. Until the chunk lands the page simply scrolls
      // natively, which is why the instance is created *after* the import
      // rather than being starved of a rAF loop while it resolves.
      const [{ default: Lenis }, { gsap }, { ScrollTrigger }] =
        await Promise.all([
          import("lenis"),
          import("gsap"),
          import("gsap/ScrollTrigger"),
        ]);

      // Strict Mode runs this effect twice; the first pass has already been
      // cancelled by the time its import resolves. The ref check additionally
      // guards a start()/start() race from a reduced-motion toggle.
      if (cancelled || lenisRef.current) return;

      gsap.registerPlugin(ScrollTrigger);

      const lenis = new Lenis({
        // Duration + easing (rather than lerp) mode: each wheel/key impulse
        // retargets an eased animation. The default easing is an expo-out,
        // so motion starts immediately and coasts to a stop instead of
        // cutting off — the momentum feel — while 1.2s keeps the tail short
        // enough that the page never feels detached from the input.
        duration: 1.2,
        smoothWheel: true,
        // Native touch scrolling is left alone. iOS/Android already apply
        // their own momentum, and `syncTouch` re-implements it in JS at a
        // real cost in jank on mid-range phones.
        syncTouch: false,
        // Let inner overflow containers (dropdown panels, the chat widget,
        // anything future) consume the gesture natively instead of scrolling
        // the page behind them. Lenis caches the computed style per node for
        // 2s, so this is not a per-event layout read.
        allowNestedScroll: true,
        // Smooth `href="#section"` clicks through Lenis rather than letting
        // the browser jump. Bare `href="#"` placeholders are untouched —
        // Lenis only intercepts same-path URLs that actually carry a hash.
        anchors: true,
        // Drop leftover momentum when navigating to another page so the new
        // route does not inherit the previous one's velocity.
        stopInertiaOnNavigate: true,
        // The rAF loop below is GSAP's ticker, not Lenis's own.
        autoRaf: false,
      });

      lenisRef.current = lenis;

      // Keep ScrollTrigger reading the position Lenis just wrote, in the same
      // frame. Wrapped rather than passed by reference: Lenis calls listeners
      // with the instance, and `ScrollTrigger.update(truthyArg)` means
      // "force".
      const onScroll = (): void => {
        ScrollTrigger.update();
      };
      lenis.on("scroll", onScroll);

      // One rAF loop for the whole page: GSAP's ticker drives Lenis, so the
      // scroll position is updated before ScrollTrigger reads it and there is
      // no second competing loop. GSAP's ticker takes seconds, Lenis takes ms.
      const tick = (time: number): void => {
        lenis.raf(time * 1000);
      };
      gsap.ticker.add(tick);
      // lagSmoothing lets GSAP clamp a long frame gap, which desynchronises a
      // scrubbed timeline from the real scroll position after a stall.
      gsap.ticker.lagSmoothing(0);

      teardown = () => {
        gsap.ticker.remove(tick);
        gsap.ticker.lagSmoothing(500, 33); // GSAP's own defaults
        lenis.off("scroll", onScroll);
        lenis.destroy();
        lenisRef.current = null;
      };
    };

    const stop = (): void => {
      teardown?.();
      teardown = null;
    };

    // Lenis 1.3 exposes `isReducedMotion` but does not act on it, so honouring
    // the preference means simply not running an instance — the page falls
    // back to plain native scrolling. Re-evaluated on change so toggling the
    // OS setting takes effect without a reload.
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const sync = (): void => {
      if (media.matches) stop();
      else void start();
    };

    sync();
    media.addEventListener("change", sync);

    return () => {
      cancelled = true;
      media.removeEventListener("change", sync);
      stop();
    };
  }, [enabled]);

  return (
    <SmoothScrollContext.Provider value={lenisRef}>
      {children}
    </SmoothScrollContext.Provider>
  );
};
